import { PublicApi, PrivateApi } from '@shared/api-client'
import {
  getLangCodeByLanguage,
  toListOfNonNullishKeyValueObjects,
  toObjectOfNonNullishEntries,
} from '@shared/utils'
import useUserStore from '@shared/stores/useUserStore.js'
import Corporate from '@shared/data/models/corporate.js'
import { assignLocaleMessage } from '@shared/localization.js'
import useCorporateStore from '@shared/stores/useCorporateStore.js'

/** @typedef {import("axios").AxiosResponse} AxiosResponse */
/** @typedef {import("@shared/data/models/user").default} User */

/** @returns { Promise<User> } */
export const login = async (email, password) => {
  const response = await PublicApi.post('v2/auth/sign_in/', { email, password })
  return await useUserStore().storeSession(response)
}

/** @returns { Promise<User> } */
export const signUp = async ({
  email,
  password,
  firstName: first_name,
  lastName: last_name,
  insuranceType,
  phoneNumber: phone_number,
  dateOfBirth: date_of_birth,
  language,
  country,
  timezone,
  acceptedDataProtection: accepted_data_protection,
  acceptedTracking: accepted_tracking,
  corporateId: corporate_id,
  corporateEligibilityData = {},
  fromApp: from_app, // from session storage
  verificationEmail,
  eligibilitySessionHash: eligibility_session_hash,
  lookupKey: solera_lookup_key,
  landingPageIncentive: landing_page_incentive,
  signupContext,
}) => {
  const response = await PublicApi.post('v2/auth/sign_up/', {
    email,
    password,
    first_name,
    corporate_id,
    language,
    country,
    timezone,
    accepted_data_protection,
    accepted_tracking,
    ...(last_name && { last_name }),
    ...(phone_number && { phone_number }),
    ...(eligibility_session_hash && { eligibility_session_hash }),
    ...(solera_lookup_key && { solera_lookup_key }),
    ...(insuranceType && {
      insurance_type:
        insuranceType === 'private' ? 'private_insurance' : 'public_insurance',
    }),
    signup_context: {
      web_entry_point_url: signupContext.webEntryPointUrl,
      web_entry_point_referrer_url: signupContext.webEntryPointReferrerUrl,
      how_did_you_hear_about_us: signupContext.howDidYouHearAboutUs,
    },
    feedback: [
      ...toListOfNonNullishKeyValueObjects({
        date_of_birth,
        from_app,
        email: verificationEmail,
        landing_page_incentive,
        ...corporateEligibilityData,
      }),
    ],
  })
  return await useUserStore().storeSession(response)
}

/** @returns { Promise<User> } */
export const loginWithOneTimeAuthToken = async (token) => {
  const response = await PublicApi.post('/1fa/auth/sign_in/token', { token })
  return await useUserStore().storeSession(response)
}

export const getOneTimeAuthToken = (code_challenge) =>
  PrivateApi.get('v2/auth/token', {
    params: code_challenge ? { code_challenge } : undefined,
  })

export const subscribeToDvg = ({ code, corporateId: corporate_id }) =>
  PrivateApi.post('v2/subscriptions/subscribe_to_dvg', { code, corporate_id })

export const sendVerificationData = ({ participationId }) =>
  PrivateApi.post('v2/user/add_feedback/', {
    feedback: [{ key: 'participation_id', value: participationId }],
  })

// currently only used for generali in the EU, to validate whether this would also work for eligibility list in the US
export const triggerCorporateSubscriptionCreationOrProlongation = ({
  corporateKey: corporate_key,
  eligibilityParams: eligibility_check = {},
  eligibilitySessionHash: eligibility_session_hash,
}) => {
  return PrivateApi.post('v2/user/partner_subscriptions/', {
    corporate_key,
    eligibility_check,
    eligibility_session_hash,
  })
}

export const uploadPrescriptionFile = (formData, params) => {
  params = Object(params) === params ? params : {}

  return PrivateApi.post('v2/user/prescriptions', formData, params)
}

export const getPopularCorporates = () => PublicApi.get('v2/popular_corporates')

export const sendHcpInformation = ({ name, city, zip }) =>
  PrivateApi.post('v2/user/hcp_information', {
    name,
    city,
    ...(typeof zip === 'string' && zip.length > 0 ? { zip } : {}),
  })

export const sendSubscriptionActivationEntry = ({ type, attributes }) =>
  PrivateApi.post('v2/subscription_activation_entries', {
    data: {
      type,
      attributes,
    },
  })

export const sendUserFeedback = ({ dateOfBirth }) =>
  PrivateApi.post('v2/user/add_feedback/', {
    feedback: [{ key: 'date_of_birth', value: dateOfBirth }],
  })

/** @returns { Promise<User> } */
export const subscribeUserWithVoucher = async (code) => {
  await PrivateApi.post('v2/payment/subscribe_with_corporate_voucher', { code })
  return await getUser()
}

export const checkVoucher = (code) =>
  PublicApi.post('v2/check_corporate_voucher/', { code })

/** @returns { Promise<Corporate> } */
export const loadCorporate = async ({ key, id }) => {
  const corporateStore = useCorporateStore()
  const lang = useUserStore().lang

  let corporateInstance = corporateStore.corporate
  // Only fetch if not already loaded
  if (
    (key && corporateInstance?.key !== key) ||
    (id && corporateInstance?.id !== id)
  ) {
    const url = key
      ? `v2/content/get_corporate?key=${key}&language=${lang}`
      : `v2/content/get_corporate?id=${id}&language=${lang}`
    const response = await PublicApi.get(url)
    corporateInstance = new Corporate(response.data)
  }

  if (corporateInstance?.translations) {
    assignLocaleMessage(
      corporateInstance.translations,
      getLangCodeByLanguage(lang),
    )
    corporateStore.corporate = corporateInstance
  }
  return corporateInstance
}

/** @returns { Promise<User> } */
export const getUser = async () => {
  const response = await PrivateApi.get('1fa/user')
  return await useUserStore().storeSession(response)
}

/** @returns { Promise<User> } */
export const setUserProfile = async ({
  firstName: first_name,
  lastName: last_name,
  acceptedDataProtection: accepted_data_protection,
  acceptedTracking: accepted_tracking,
  phoneNumber: phone_number,
  corporateId: corporate_id,
}) => {
  const response = await PrivateApi.patch(
    '1fa/profile',
    toObjectOfNonNullishEntries({
      first_name,
      last_name,
      accepted_data_protection,
      accepted_tracking,
      phone_number,
      corporate_id,
    }),
  )
  return await useUserStore().storeSession(response)
}

/** @returns { Promise<User> } */
export const setUserEmail = async (email) => {
  const response = await PrivateApi.post('v2/user/set_email', { email })
  return await useUserStore().storeSession(response)
}

export const getCorporateEligibilityConfig = (corporateKey, lang) =>
  PublicApi.get(
    `v2/corporates/${corporateKey}/eligibility_config?language=${lang}`,
  )

export const checkCorporateEligibility = (corporateEligibilityData) =>
  PublicApi.get('v2/check_corporate_eligibility', {
    params: corporateEligibilityData,
  })

export const getCorporateEligibilityGroupConfig = (groupId, lang) =>
  PublicApi.get(
    `v2/corporate_eligibility_groups/${groupId}/eligibility_config?language=${lang}`,
  )

export const checkCorporateGroupEligibility = (corporateGroupEligibilityData) =>
  PublicApi.get('v2/check_corporate_group_eligibility', {
    params: corporateGroupEligibilityData,
  })

export const checkEligibilityWithEligibleToken = (
  eligibility_session_hash,
  eligible_token,
) =>
  PublicApi.post('v2/check_eligibility/with_token', {
    eligibility_session_hash,
    eligible_token,
  })

/**
 * @param { string } locale of the faqs, 'en-US' or 'es-US'
 * @typedef { id: string, title: string, order: number, text: string} Faq
 * @typedef { count: number, faqs: Faq[] } FaqList
 * @returns { Promise<AxiosResponse<{ count: number, faqs: Faq[] }>> }: count of faqs and list of faqs
 */
export const getHelpscoutFaqs = async (locale) =>
  await PublicApi.get('v2/support/faqs', {
    params: { locale },
  })

export const submitSupportTicket = async (data) =>
  await PublicApi.post('v2/support/tickets', data)

export const getCheckups = () => PrivateApi.get('1fa/user_checkups')

export const submitSelfTest = (data) =>
  PrivateApi.post('v2/user/complete_self_test', data)

export const submitSelfTestReevaluations = (data) =>
  PrivateApi.post('v2/user/assessment_questionnaire_results', {
    assessment_questionnaire_results: {
      self_test_reevaluation: data,
    },
  })

export const submitCAT = (data) =>
  PrivateApi.post('v2/user/assessment_questionnaire_results', {
    assessment_questionnaire_results: {
      cat: data,
    },
  })

export const searchCorporate = (search, country, abortSignal) =>
  PublicApi.get('v2/search_corporate', {
    params: { search, ...(country ? { country } : {}) },
    signal: abortSignal,
  })

/** @returns { Promise<User> } */
export const subscribeWithInsuranceToken = async (
  corporateKey,
  token,
  type,
) => {
  await PrivateApi.post('v2/payment/subscribe_with_insurance_token', {
    corporate_name: corporateKey,
    token,
    type,
  })
  return await getUser()
}

/** @returns { Promise<User> } */
export const subscribeWithEligibleToken = async (verificationData) => {
  await PrivateApi.post(
    'v2/payment/subscribe_with_eligible_token',
    verificationData,
  )
  return await getUser()
}

/** @returns { Promise<User> } */
export const subscribeWithVerificationData = async (verificationData) => {
  await PrivateApi.post(
    'v2/payment/subscribe_with_verification_data',
    verificationData,
  )
  return await getUser()
}

export const sendBrazeEvent = ({ name, properties }) => {
  if (name === null || name === undefined) {
    return Promise.reject({
      id: 1,
      message: `sendBrazeEvent: expected ${name} to be a string;`,
    })
  }

  const payload = { name }

  if (properties !== null && properties !== undefined) {
    payload.properties = properties
  }

  return PrivateApi.post('v2/user/send_crm_event', payload)
}

export const resetPassword = (reset_password_token, password, baseURL) =>
  PublicApi.post(
    'auth/password/change_with_reset_token',
    {
      reset_password_token,
      password,
    },
    { baseURL: baseURL || PublicApi.defaults.baseURL },
  )
